<template>
  <AuthLayout>
    <div class="auth-header">
      <RouterLink to="/login" class="back-link">
        <i class="pi pi-arrow-left" aria-hidden="true" /> Volver al login
      </RouterLink>
      <h2>Recuperar contraseña</h2>
      <p>Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.</p>
    </div>

    <!-- Formulario -->
    <form v-if="!sent" class="auth-form" novalidate @submit.prevent="submit">
      <div class="field">
        <label for="forgot-email">Correo electrónico</label>
        <InputText
          id="forgot-email"
          v-model="email"
          type="email"
          placeholder="tu@email.com"
          autocomplete="email"
          autofocus
          :invalid="!!error"
          aria-describedby="forgot-email-error"
          @input="error = ''"
        />
        <small v-if="error" id="forgot-email-error" class="field-error">
          <i class="pi pi-exclamation-circle" aria-hidden="true" /> {{ error }}
        </small>
      </div>

      <Button
        label="Enviar enlace de recuperación"
        icon="pi pi-send"
        type="submit"
        class="w-full"
        :loading="loading"
      />
    </form>

    <!-- Confirmación -->
    <div v-else class="sent-msg">
      <i class="pi pi-check-circle sent-icon" aria-hidden="true" />
      <h3>Revisa tu correo</h3>
      <p>
        Si <strong>{{ email }}</strong> está registrado en Nexo,
        recibirás un enlace para restablecer tu contraseña en los próximos minutos.
      </p>
      <p class="hint">Revisa también tu carpeta de spam.</p>

      <!-- En desarrollo: mostrar el token directamente -->
      <div v-if="devToken" class="dev-box">
        <p><strong>🛠️ Modo desarrollo</strong> — SMTP no configurado</p>
        <p>Usa este enlace para restablecer la contraseña:</p>
        <a :href="devUrl" class="dev-link">{{ devUrl }}</a>
      </div>

      <Button
        label="Volver al login"
        icon="pi pi-sign-in"
        class="w-full"
        @click="$router.push('/login')"
      />
    </div>
  </AuthLayout>
</template>

<script setup>
import { ref } from 'vue';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import AuthLayout from '../components/layout/AuthLayout.vue';
import api from '../services/api';

const email = ref('');
const loading = ref(false);
const error = ref('');
const sent = ref(false);
const devToken = ref('');
const devUrl = ref('');

const submit = async () => {
  if (!email.value.trim()) { error.value = 'Ingresa tu correo electrónico'; return; }
  loading.value = true; error.value = '';
  try {
    const res = await api.post('/auth/forgot-password', { email: email.value });
    sent.value = true;
    // En desarrollo el backend devuelve el token directamente
    if (res.data.dev_token) {
      devToken.value = res.data.dev_token;
      devUrl.value = res.data.dev_reset_url;
    }
  } catch (e) {
    error.value = e.response?.data?.detail || 'Error al procesar la solicitud';
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.auth-header {
  margin-bottom: 1.5rem;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  color: #6b7280;
  text-decoration: none;
  margin-bottom: 1rem;
}

.back-link:hover {
  color: #6366f1;
}

.auth-header h2 {
  margin: 0 0 0.4rem;
  color: #1e293b;
  font-size: 1.45rem;
  font-weight: 700;
}

.auth-header p {
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.field label {
  font-weight: 600;
  font-size: 0.875rem;
  color: #374151;
}

.field :deep(.p-inputtext) {
  width: 100%;
}

.field-error {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: #dc2626;
  font-size: 0.78rem;
}

.w-full {
  width: 100%;
}

.sent-msg {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.sent-icon {
  font-size: 3.5rem;
  color: #10b981;
}

.sent-msg h3 {
  margin: 0;
  color: #1e293b;
}

.sent-msg p {
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.hint {
  font-size: 0.78rem !important;
  color: #9ca3af !important;
}

.dev-box {
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 0.875rem;
  text-align: left;
  width: 100%;
  font-size: 0.8rem;
}

.dev-box p {
  margin: 0.2rem 0;
  color: #92400e;
}

.dev-link {
  color: #6366f1;
  word-break: break-all;
  font-size: 0.75rem;
}
</style>
